import React, { useState } from 'react';
import { Popover } from 'antd';
import { Metric, ProductPPG, PromoConfigDictionary } from 'types/tradePromoOptimization';
import { formatNumberWithCommas } from 'utils/helperMethods';
import { KPIItem } from '../KPISelector';
import EditableMetricInput from './EditableMetricInput';

type TotalsCellProps = {
    ppgs: ProductPPG[];
    headerKPIs: KPIItem[];
    promoConfigDictionary: PromoConfigDictionary;
    isInputDisabled: (metricId: number) => boolean;
    combinedViewMode: boolean;
    editingCell: { metricId: number; ppgName: string } | null;
    setEditingCell: (v: any) => void;
    editValue: string;
    setEditValue: (v: string) => void;
    isValidMetricInput: (value: string, metricConfig: any, setWarningMessage: (msg: string) => void) => boolean;
    handleInputChange: (metricId: number, ppgName: string, value: string) => void;
    metricType: 'spendDetails' | 'metrics';
};

const TotalsCell: React.FC<TotalsCellProps> = ({
    ppgs,
    headerKPIs,
    promoConfigDictionary,
    isInputDisabled,
    combinedViewMode,
    editingCell,
    setEditingCell,
    editValue,
    setEditValue,
    isValidMetricInput,
    handleInputChange,
    metricType,
}) => {
    const [warningMessage, setWarningMessage] = useState('');
    const targetTotal: Record<number, any | null> = {};

    ppgs.filter((p: ProductPPG) => p.isSelected).forEach((p: ProductPPG) => {
        const targetMetrics = p[metricType]; // promoMetrics or spendMetrics
        const type: 'promoMetrics' | 'spendMetrics' = metricType === 'metrics' ? 'promoMetrics' : 'spendMetrics';
        const metricConfig = promoConfigDictionary?.[type];

        targetMetrics.forEach((metric: Metric) => {
            const config = metricConfig?.[metric.id];
            if (config?.isLumpsum) {
                if (!targetTotal[metric.id]) {
                    targetTotal[metric.id] = 0;
                }
                targetTotal[metric.id]! += Number(metric.value) ?? 0;
            }
        });
    });

    return (
        <div>
            {headerKPIs
                ?.filter((metric) => metric.isSelected && !metric.isHidden)
                .map((headerMetric) => {
                    const metric = ppgs[0]?.[metricType].find((m: Metric) => m.id.toString() === headerMetric.value);
                    if (!metric) return null;

                    const isDisabled = isInputDisabled(metric.id);
                    const isEditing = editingCell?.metricId === metric.id && editingCell?.ppgName === 'total';
                    const type: 'promoMetrics' | 'spendMetrics' =
                        metricType === 'metrics' ? 'promoMetrics' : 'spendMetrics';
                    const metricConfig = promoConfigDictionary?.[type][metric.id];
                    const prefix = metricConfig?.preUnit;
                    const suffix = metricConfig?.postUnit;
                    const allowedDecimals = metricConfig?.allowedDecimals;
                    const isTotalsColumn = metricConfig.isLumpsum;

                    return (
                        <div
                            key={metric.id}
                            className={`flex flex-col min-w-[200px] max-w-[200px] ${
                                isTotalsColumn ? 'bg-white' : 'bg-backgroundGreyAlt'
                            }  `}
                        >
                            <div className="h-9 border-l border-r border-b font-xs border-backgroundGreyAlt1 flex items-center">
                                <div
                                    className={`flex justify-between items-center w-full h-full ${
                                        isEditing ? '' : 'px-2 py-1 '
                                    } ${combinedViewMode && !isTotalsColumn ? 'cursor-default' : 'cursor-pointer'} ${
                                        isDisabled ? 'text-backgroundGreyAlt2' : ''
                                    }`}
                                    tabIndex={0}
                                    onClick={() => {
                                        if (!isDisabled && !combinedViewMode && isTotalsColumn) {
                                            setEditingCell({ metricId: metric.id, ppgName: 'total' });
                                            setEditValue(targetTotal[metric.id]);
                                        }
                                    }}
                                >
                                    {isEditing && isTotalsColumn ? (
                                        <EditableMetricInput
                                            value={editValue}
                                            warningMessage={warningMessage}
                                            isEditing={isEditing}
                                            onChange={(val) => {
                                                setEditValue(val);
                                                isValidMetricInput(val, metricConfig, setWarningMessage);
                                            }}
                                            onValidate={(val) =>
                                                isValidMetricInput(val, metricConfig, setWarningMessage)
                                            }
                                            onSubmit={() => handleInputChange(metric.id, 'total', editValue)}
                                            onCancel={() => {
                                                setEditingCell(null);
                                                setEditValue('');
                                                setWarningMessage('');
                                            }}
                                        />
                                    ) : (
                                        <div className="flex justify-between items-center w-full">
                                            <div className="truncate w-full">
                                                {isTotalsColumn ? (
                                                    <>
                                                        {prefix}
                                                        {formatNumberWithCommas(
                                                            Number(targetTotal[metric.id]),
                                                            allowedDecimals,
                                                        )}
                                                        {suffix}
                                                    </>
                                                ) : (
                                                    '-'
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};

export default TotalsCell;
